<template>
  <div v-if="loaded">
    <FormKit v-model="taskAttrs" type="form" class="mt-4" :actions="false">
      <FormKit type="text" name="name" label="Task Name" />
      <QuillInput
        label="Task Description"
        name="task-description"
        :model-value="taskAttrs.description"
        @update:model-value="taskAttrs.description = $event"
      />
      <FormKit
        type="select"
        label="Assigned to"
        name="assigned_to_role"
        :options="{ agent: 'Agent', other: 'Other' }"
        placeholder="Select a person"
      />
      <FormKit
        v-if="taskAttrs.assigned_to_role === 'other'"
        type="select"
        label="Assigned to"
        name="assigned_to_user_id"
        :options="assignableUsers"
        placeholder="Select a person"
      />

      <div v-if="task.file_objects.length > 0" class="mt-6 flow-root">
        <ul role="list" class="-mb-8">
          <li
            v-for="file of task.file_objects"
            :key="file.name"
            class="pb-4 pl-3 pr-4 py-3 flex items-center justify-between text-sm"
          >
            <div class="w-0 flex-1 flex items-center">
              <PaperclipIcon class="flex-shrink-0 h-5 w-5 text-gray-400" />
              <span class="ml-2 flex-1 w-0 truncate">{{ file.name }}</span>
            </div>
            <div class="ml-4 flex-shrink-0">
              <ButtonMenu :icon-only="true" :sections="2">
                <template #topmenu>
                  <a
                    target="_blank"
                    :href="file.url"
                    class="cursor-pointer group flex items-center px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                    role="menuitem"
                  >
                    <svg
                      class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500 group-focus:text-gray-500"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="2"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                      />
                    </svg>
                    View
                  </a>
                </template>
                <template #middlemenu>
                  <a
                    class="cursor-pointer group flex items-center px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                    role="menuitem"
                    @click="deleteAttachment(file)"
                  >
                    <svg
                      class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500 group-focus:text-gray-500"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      stroke-width="2"
                      stroke="currentColor"
                      fill="none"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                      <line x1="4" y1="7" x2="20" y2="7" />
                      <line x1="10" y1="11" x2="10" y2="17" />
                      <line x1="14" y1="11" x2="14" y2="17" />
                      <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" />
                      <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" />
                    </svg>
                    Delete
                  </a>
                </template>
              </ButtonMenu>
            </div>
          </li>
        </ul>
      </div>
      <FileInput
        v-else
        label="Attachments"
        name="files"
        limits="any file"
        :multiple="true"
        accept="*"
        :model-value="taskAttrs.files"
        @update:model-value="taskAttrs.files = $event"
      />
    </FormKit>
  </div>
</template>

<script>
import ButtonMenu from '../shared/ButtonMenu.vue';
import QuillInput from '../shared/QuillInput.vue';
import { toast } from 'vue-sonner';

export default {
  components: {
    ButtonMenu,
    QuillInput,
  },
  props: {
    task: {
      required: true,
      type: Object,
    },
    assignableUsers: {
      required: true,
      type: Array,
    },
  },
  data() {
    return {
      loaded: false,
      taskAttrs: {},
    };
  },
  mounted() {
    this.taskAttrs = { ...this.task, files: [] };
    this.loaded = true;
  },
  methods: {
    async updateTask() {
      let formData = new FormData();

      if (this.taskAttrs.files.length > 0) {
        for (let i = 0; i < this.taskAttrs.files.length; i++) {
          formData.append('checklist_task[files][]', this.taskAttrs.files[i], this.taskAttrs.files[i].name);
        }
      }

      formData.append('checklist_task[name]', this.taskAttrs.name);
      formData.append('checklist_task[description]', this.taskAttrs.description);
      formData.append('checklist_task[assigned_to_role]', this.taskAttrs.assigned_to_role);
      formData.append('checklist_task[assigned_to_user_id]', this.taskAttrs.assigned_to_user_id);
      formData.append('checklist_task[order]', this.taskAttrs.order);

      const res = await fetch(`/api/task_checklists/${this.task.task_checklist_id}/checklist_tasks/${this.task.id}`, {
        method: 'PUT',
        headers: {
          'X-CSRF-TOKEN': document.querySelectorAll('[name="csrf-token"]')[0].content,
        },
        body: formData,
      });

      const { status } = await res.json();

      this._goBackAndShowAlertWhenSuccess({
        status,
        alertTitle: 'Task Updated',
        alertDescription: 'Your task was successfully updated.  Use the button below to dismiss this alert.',
      });
    },
    async fetchEsignatureTemplates() {
      const res = await fetch(`/api/esignature_templates`);
      const { records } = await res.json();

      this.templates = records;
    },
    async deleteAttachment(attachment) {
      const { status } = await this.$api.Attachments.deleteAttachment(attachment);
      this._goBackAndShowAlertWhenSuccess({
        status,
        alertTitle: 'Attachment Deleted',
        alertDescription: 'Your attachment was successfully edited.  Use the button below to dismiss this alert.',
      });
    },
    async _goBackAndShowAlertWhenSuccess({ status, alertTitle, alertDescription }) {
      if (status === 'success') {
        const checklistType = this.task.task_checklist.checklist_type;
        await this.$router.push(`/settings/company/${checklistType}_checklists/${this.task.task_checklist_id}/edit`);
        toast.success(alertTitle, {
          action: { label: 'Dismiss' },
          description: alertDescription,
        });
      }
    },
  },
};
</script>
